import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  LinearProgress,
} from '@mui/material';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout, selectSignup } from '../../store/signupSlice';
import { selectOnboarding } from '../../store/onboardingSlice';
import Logo from '../common/Logo';

const OnboardingLayout: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { companyName } = useAppSelector(selectSignup);
  const { currentStep, loading } = useAppSelector(selectOnboarding);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Calculate progress based on step (5 steps total)
  const progress = (currentStep / 5) * 100;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <AppBar position="fixed" elevation={0}>
        <Toolbar>
          <Logo size="small" />
          <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
            Setup: {companyName}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout} title="Logout">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 2,
            }}
          />
        )}
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 10, md: 12 },
          pb: 4,
        }}
      >
        <Container maxWidth="lg">
          {/* Progress indicator */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Step {currentStep} of 5
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}% complete
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #64b5f6 0%, #90caf9 100%)',
                },
              }}
            />
          </Box>

          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default OnboardingLayout;
